import { Badge, Box, Button, Stack } from "@mui/material";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@iconify/react";
import dayjs from "dayjs";
import { useFormik } from "formik";

import {
  getAllGoals,
  getAlltagnames,
  getGoalStatusesForFilterTabs,
} from "../../../../../redux/features/appraisalSlice";

import UserProfile from "./UserProfile";
import FilterSections from "./FilterSections";
import FilterCards from "./FilterCards";
import GoalView from "./GoalView";
import PaginationComponent from "../../../../appraisal/goalManagement/PaginationComponent";
import useResponsive from "../../../../../Hooks/useResponsive";
import MotionWrapper from "../../../../../components/MotionWrapper";
import LoadingSkelton from "./LoadingSkelton";
import NoDataFound from "../../../../../components/NodataIndication/NoDataFound";
import Approval from "./Approval";
import Chart from "./Chart";

const TeamGoal = ({ state }) => {
  const dispatch = useDispatch();
  const smUp = useResponsive("up", "sm");
  const mdUp = useResponsive("up", "md");

  const { loading, goalData, goalStatuses, pendingEditRequestCount } = useSelector(
    (state) => state.appraisal
  );

  const [expandAll, setExpandAll] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [selectedGoals, setSelectedGoals] = useState([]);

  const formik = useFormik({
    initialValues: {
      user: { userID: -1, name: "View All" },
      fromDate: null,
      toDate: null,
      search: "",
      goalStatus: -1,
      archiveViewChecked: false,
      type: state.viewType || 1,
      tags: "",
      // Narrows the list to goals with a pending edit request awaiting this
      // reporting head's decision - separate from goalStatus, which is
      // about the goal's original creation approval.
      needsEditApproval: false,
    },
    onSubmit: (values) => {
      dispatch(getGoalStatusesForFilterTabs(values));
      dispatch(getAllGoals(values));
    },
  });
  const prevGoalStatusRef = useRef(formik.values.goalStatus);
  const prevUserRef = useRef(formik.values.user);
  const prevNeedsEditApprovalRef = useRef(formik.values.needsEditApproval);

  useEffect(() => {
    dispatch(getGoalStatusesForFilterTabs(formik.values));
    dispatch(getAlltagnames());
  }, []);

  useEffect(() => {
    if (prevGoalStatusRef.current !== formik.values.goalStatus) {
      prevGoalStatusRef.current = formik.values.goalStatus;
      dispatch(getGoalStatusesForFilterTabs(formik.values));
      dispatch(getAllGoals(formik.values));
    }
  }, [formik.values.goalStatus]);

  useEffect(() => {
    if (prevNeedsEditApprovalRef.current !== formik.values.needsEditApproval) {
      prevNeedsEditApprovalRef.current = formik.values.needsEditApproval;
      dispatch(getAllGoals(formik.values));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.needsEditApproval]);

  useEffect(() => {
    if (prevUserRef.current?.userID !== formik.values.user?.userID) {
      prevUserRef.current = formik.values.user;
      dispatch(getGoalStatusesForFilterTabs(formik.values));
    }
  }, [formik.values.user]);

  const toggleAll = useCallback(() => {
    const newExpandState = !expandAll;
    const newExpandedItems = {};
    goalData?.data?.forEach((goal) => {
      newExpandedItems[goal.goal_id] = newExpandState;
    });
    setExpandAll(newExpandState);
    setExpandedItems(newExpandedItems);
  }, [expandAll, goalData?.data]);

  const handleAccordionChange = useCallback(
    (goalId) => (_, isExpanded) => {
      setExpandedItems((prev) => ({
        ...prev,
        [goalId]: isExpanded,
      }));
    },
    []
  );

  const handleCheckboxChange = useCallback(
    (goal) => (event) => {
      setSelectedGoals((prev) => {
        if (event.target.checked) {
          return [...prev, goal];
        } else {
          return prev.filter((g) => g.goal_id !== goal.goal_id);
        }
      });
    },
    []
  );

  // Goals with a pending edit request surface at the top of the (current)
  // page, so the reporting head doesn't have to hunt for them once they've
  // switched into Team Data.
  const sortedGoalData = useMemo(() => {
    const list = goalData?.data || [];
    return [...list].sort((a, b) => {
      const aPending = a?.pendingEditRequests?.length > 0 ? 1 : 0;
      const bPending = b?.pendingEditRequests?.length > 0 ? 1 : 0;
      return bPending - aPending;
    });
  }, [goalData?.data]);

  const hasPendingGoals = useMemo(
    () =>
      goalData?.data?.some(
        (goal) => goal?.appraisal_status_name?.toLowerCase() === "pending"
      ),
    [goalData?.data]
  );

  const allSelected = useMemo(
    () =>
      goalData?.data
        ?.filter(
          (goal) => goal?.appraisal_status_name?.toLowerCase() !== "approved"
        )
        ?.every((goal) =>
          selectedGoals.some((g) => g.goal_id === goal.goal_id)
        ),
    [goalData?.data, selectedGoals]
  );

  const toggleCheckAll = useCallback(() => {
    if (allSelected) {
      setSelectedGoals([]);
    } else {
      const nonApprovedGoals = goalData.data.filter(
        (goal) => goal.appraisal_status_name.toLowerCase() !== "approved"
      );
      setSelectedGoals(nonApprovedGoals);
    }
  }, [allSelected, goalData.data]);

  return (
    <Box>
      <Stack direction={mdUp ? "row" : "column"} spacing={2}>
        <Box width={mdUp ? "60%" : "100%"}>
          <MotionWrapper direction="top" delay={0.2}>
            <UserProfile formik={formik} />
          </MotionWrapper>
        </Box>
        <Box width={mdUp ? "40%" : "100%"}>
          <MotionWrapper direction="right" delay={0.4}>
            <FilterSections formik={formik} />
          </MotionWrapper>
        </Box>
      </Stack>

      <Stack direction={mdUp ? "row" : "column"}>
        <Box width={mdUp ? "60%" : "100%"}>
          <FilterCards formik={formik} />
          <Box display="flex" justifyContent="flex-end" gap={1} my={1}>
            <Badge
              badgeContent={pendingEditRequestCount}
              color="error"
              overlap="rectangular"
              sx={{ "& .MuiBadge-badge": { right: 6, top: 4 } }}
            >
              <Button
                onClick={() => formik.setFieldValue("needsEditApproval", !formik.values.needsEditApproval)}
                startIcon={<Icon icon="mdi:pencil-clock" />}
                variant={formik.values.needsEditApproval ? "contained" : "outlined"}
                color="warning"
                size="small"
                sx={{ borderRadius: 4 }}
              >
                {formik.values.needsEditApproval ? "Showing: Goal Edit Requests" : "Goal Edit Requests"}
              </Button>
            </Badge>

            <Button
              onClick={toggleAll}
              startIcon={
                expandAll ? (
                  <Icon icon="iconoir:collapse" />
                ) : (
                  <Icon icon="formkit:expand" />
                )
              }
              variant="outlined"
              size="small"
              sx={{ borderRadius: 4 }}
            >
              {expandAll ? "Collapse All" : "Expand All"}
            </Button>

            {hasPendingGoals && (
              <Button
                onClick={toggleCheckAll}
                startIcon={
                  allSelected ? (
                    <Icon icon="ci:checkbox-unchecked" />
                  ) : (
                    <Icon icon="material-symbols:check-rounded" />
                  )
                }
                variant="outlined"
                color="secondary"
                size="small"
                sx={{ borderRadius: 4 }}
              >
                {allSelected ? "Uncheck All" : "Check All"}
              </Button>
            )}

            {selectedGoals.length > 0 && (
              <Approval
                setSelectedGoals={setSelectedGoals}
                props={formik.values}
                meta={goalData.meta}
                selectedGoals={selectedGoals}
              />
            )}
          </Box>

          {loading ? (
            <LoadingSkelton />
          ) : sortedGoalData.length > 0 ? (
            sortedGoalData.map((goal, i) => (
              <MotionWrapper key={goal.goal_id} direction="top" delay={i * 0.1}>
                <GoalView
                  expanded={expandedItems[goal.goal_id] || false}
                  onChange={handleAccordionChange(goal.goal_id)}
                  goals={goal}
                  isChecked={selectedGoals.some(
                    (g) => g.goal_id === goal.goal_id
                  )}
                  onCheck={handleCheckboxChange(goal)}
                  formikValues={formik.values}
                />
              </MotionWrapper>
            ))
          ) : (
            <MotionWrapper direction="top" delay={0.4}>
              <NoDataFound
                message={
                  formik.values.needsEditApproval
                    ? "No goals currently need edit approval. Approving or rejecting the last one moves it out of this filter - not a data loss."
                    : undefined
                }
              />
            </MotionWrapper>
          )}

          <Box pt={2}>
            <PaginationComponent
              api={getAllGoals}
              otherProps={formik.values}
              loading={loading}
              meta={goalData.meta}
            />
          </Box>
        </Box>
        <Box width={mdUp ? "40%" : "100%"}>
          <Chart />
        </Box>
      </Stack>
    </Box>
  );
};

export default TeamGoal;
